import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  BookOpenCheck, ShieldCheck, Landmark, Bus, GraduationCap, HeartPulse, Hospital,
  Building2, Scale, FileText, HelpCircle, Search, ArrowRight, Sparkles,
} from "lucide-react";

const ICON_BY_SLUG = {
  "direitos-da-crianca-neuroatipica": ShieldCheck,
  "beneficios-do-inss": Landmark,
  "bpc": Landmark,
  "passe-livre": Bus,
  "direitos-escolares": GraduationCap,
  "direitos-na-saude": HeartPulse,
  "direitos-no-sus": Hospital,
  "direitos-perante-os-planos-de-saude": Building2,
  "leis-importantes": Scale,
  "modelos-de-requerimentos": FileText,
  "perguntas-frequentes": HelpCircle,
};

export default function Rights() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/rights").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const filtered = (items || []).filter((r) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    return (
      r.title?.toLowerCase().includes(t) ||
      r.category?.toLowerCase().includes(t) ||
      r.summary?.toLowerCase().includes(t)
    );
  });

  return (
    <div>
      <section className="relative overflow-hidden border-b border-[hsl(var(--border))] bg-gradient-to-br from-[hsl(var(--primary))]/8 via-white to-[hsl(var(--secondary))]/8">
        <div className="container-cuidar py-14">
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold tracking-widest uppercase text-[hsl(var(--secondary))]">
            <Sparkles className="h-3.5 w-3.5" /> Área de Direitos
          </div>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-tight max-w-3xl">
            Todos os direitos da sua família, em um só lugar
          </h1>
          <p className="mt-5 max-w-2xl text-[hsl(var(--muted-foreground))]">
            Guia prático para navegar leis, benefícios, laudos e requerimentos — escrito em linguagem
            clara pela nossa equipe. Conteúdos revisados e atualizados regularmente.
          </p>
          <div className="mt-8 relative max-w-lg">
            <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por benefício, lei ou palavra-chave…"
              className="h-12 pl-10 rounded-full border-[hsl(var(--border))] bg-white shadow-sm"
              data-testid="rights-search-input"
            />
          </div>
        </div>
      </section>

      <section className="container-cuidar py-14">
        {items === null && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="surface p-6 animate-pulse h-52 bg-[hsl(var(--muted))]" />
            ))}
          </div>
        )}

        {items !== null && filtered.length === 0 && (
          <div className="surface p-10 text-center" data-testid="rights-empty">
            <BookOpenCheck className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto" />
            <div className="mt-3 font-heading font-bold">Nada encontrado</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Nenhum tópico bate com sua busca. Tente outra palavra ou volte ao painel principal.
            </p>
          </div>
        )}

        {items !== null && filtered.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="rights-grid">
            {filtered.map((r, idx) => {
              const Icon = ICON_BY_SLUG[r.slug] || BookOpenCheck;
              return (
                <Link
                  key={r.id}
                  to={`/direitos/${r.slug}`}
                  className="group surface p-6 transition-all hover:-translate-y-1 hover:shadow-lg block"
                  data-testid={`right-card-${r.slug}`}
                  style={{ animation: `fadeIn 0.4s ${idx * 40}ms both` }}
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 h-12 w-12 rounded-2xl bg-[hsl(var(--primary))]/12 grid place-items-center text-[hsl(var(--primary))] group-hover:bg-[hsl(var(--primary))] group-hover:text-white transition-colors">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--secondary))]">
                        {r.category}
                      </div>
                      <div className="mt-1 font-heading font-extrabold text-lg leading-tight">
                        {r.title}
                      </div>
                    </div>
                  </div>
                  <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] line-clamp-3">
                    {r.summary}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))]">
                    Ler tópico completo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-14 rounded-3xl bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--primary))]/85 text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-white/70">Não achou o que procura?</div>
            <div className="mt-2 font-heading text-2xl font-black">Fale com nossa equipe pelo WhatsApp</div>
            <p className="text-sm mt-2 text-white/80 max-w-xl">
              Casos individuais podem exigir orientação personalizada. Nossa equipe está a um clique
              de distância.
            </p>
          </div>
          <a
            href="/contato"
            className="btn-pill inline-flex items-center gap-2 bg-white text-[hsl(var(--primary))] px-6 py-3 font-bold hover:bg-white/95"
            data-testid="rights-cta-contato"
          >
            Falar com a equipe <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
